import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { fitAxisOf, useHoleFit, type FitAxis } from './holeFit';
import { loadDeps } from './meshScene';
import { faceNormal, pointOnFace } from './partFaces';
import { loadSolid, type Solid } from './partSolids';
import { keptHand, productMiddleX } from './holeLines';
import { EXPLODE_MS, explodeShifts, smoothstep } from './explodePieces';
import { jointSpread } from './jointSpread';
import type { Vec3 } from './assemblyPose';
import type { HoleMark } from './drillPreview';
import { builtBoundsOf, builtBoxOf } from './openaiBlenderApi';
import { namedIn } from './partsCatalogue';
import { PartViewer, VIEWER_BOX, type ViewerOther } from './PartViewer';
import { editsOf, meshAtSrc, meshSrc, meshStamp, type Job3D } from './threeDApi';
import { PartHistories, type PartView } from './PartHistories';
import { useAssemblySteps } from './categoryBands';
import { huesByVia, SAID_COLOURS } from './holeColours';
import { useHoleAir } from './holeAir';
import { holesOfJoint, useJoints, useMeshOf, useMeshOfLoaded, type HoleGroup, type Joint } from './holeGroups';
import { usePartModelJobs } from './partModels';
import { checkCovers, checkJoint } from './jointCheck';
import { ExpectedLines, JointList, type JointStatus } from './JointChecks';
import { usePlace, writePlace } from './lastPlace';
import { useModelSizes } from './partModelSize';
import { isHardwareOrTool, isLeftRight, partSearchKey, sidedName, usePartsRun } from './partsRun';
import { holeNumber, holeOnUnit, holePlace, holeWidthOf, type StepHole } from './stepHoles';
import { holeDepthOf } from './bores';
import { groupKey, holesOfPart, useStepHoles } from './stepHolesRun';
import { useLanguage } from '../../contexts/LanguageContext';
import { coverMarks, firstContact, type CoverMark } from './covers';
import { acrossOf, BACK_OFF_MM, holeTurn, restDown, unthreaded } from './jointMoves';
import { PLAY_CARD } from './ScrubBar';
import { useCoverCollisions } from './coverCollisions';
import { useJointCoverFacts } from './CoversRun';
import { useHoles } from './FindHoles';
import type { HardwareInteraction } from './hardwareInteractionsRun';
import { dropJointView, jointPlaceSlot, registerJointView, useDrillNotes, useJointRequests } from './JointsRun';
import { keepCoverInto, keepCoverMissed, useCoverPlacements } from './coverPlacementRun';
import { Loader2 } from 'lucide-react';

// THE HOLES BY JOINT — one per Find holes group, exactly as its run menu lists them, in a list
// that says which are right: the parts that group drills, together where the product built them
// with the hardware in its holes, and each hole the hardware table expects against what was found
// (see jointCheck). What a group decided is checked here, as one thing.
//
// DRILLED ON THE PART. A bore cuts one part's mesh, and that mesh's versions are the part's own
// (see HolesTable under ThreeDStep) — so a line's part is a link to it.

// The assembled product's place in the remembered position, where a joint's id would be.
const ASSEMBLED = 'assembled';
// And the first tab's, where one is given.
const TABLED = 'table';

const dimsOf = (b: { min: number[]; max: number[] }) =>
  ({ x: b.max[0] - b.min[0], y: b.max[1] - b.min[1], z: b.max[2] - b.min[2] });

// A hole's mouth on its part's box face, from the box's centre — in the product's frame.
const placeOn = (h: StepHole, b: { min: number[]; max: number[] }): Vec3 | null => {
  const dims = dimsOf(b);
  const at = holePlace(h, dims);
  return at ? pointOnFace({ face: h.face, u: at.u, v: at.v }, dims) : null;
};

// THE HOLE EACH PIECE OF HARDWARE IS DRAWN IN — ONCE PER PIECE, in the hole it enters by: a screw's
// line holds two holes, the one its head is in (through) and the one its tip ends in; a dowel's
// two, the first declared taking it. `fits`: whether it has a model to be drawn with.
function pieceHoles(found: StepHole[], fits: (h: StepHole) => boolean): StepHole[] {
  return found.filter((h) => {
    if (h.via == null || !fits(h)) return false;
    // One line: the one it was made by (StepHole.line) — or, on an answer from before that was
    // kept, the same hardware at the same sub-step of the same step (a hole's id says its step).
    const stepOf = (o: StepHole) => Math.floor(holeNumber(o.id) / 100);
    const line = h.line
      ? found.filter((o) => o.line === h.line && o.via === h.via)
      : found.filter((o) => !o.line && o.via === h.via && o.at === h.at && stepOf(o) === stepOf(h));
    const through = line.filter((o) => o.through);
    if (through.length) return h.through && through[0].id === h.id;
    return line[0].id === h.id;
  });
}

// THE PIECES OF HARDWARE THAT CANNOT BE DRAWN FOR WANT OF WHERE THE WOOD BEGINS UNDER THEM, as
// their hole says it (StepHole.surfaceMm) — never measured instead: a ray down a drilled bore meets
// no wood. A cover is measured where it rests (see mouths), and counted where nothing was found.
const unmeasuredOf = (pieces: StepHole[]) => pieces.filter((h) => h.surfaceMm == null);

// A COVER AS DRAWN, with the call whose answer placed it (a covers joint's id).
type Drawn = CoverMark & { unit: string };
// A WASHER AS DRAWN: in the hole of the piece it goes on (`rides`), as a hole 0 deep — flat where the
// head bears, under it, as a surface cover sits on its face.
type Washer = StepHole & { rides: StepHole };
// EXPLODED, A WASHER SLID THIS FAR DOWN ITS SCREW'S SHANK, off the head — on it, it is a rim round
// the head and nobody sees it is a piece of its own.
const WASHER_GAP_MM = 10;

// COVERS A JOINT SHOWS FROM ONE CALL: whose call (a covers joint's id), and the rows they are checked against.
type Shown = { owner: string; rows: HardwareInteraction[] };



// A NOTE ABOUT ONE HOLE is headed by its id — "H103 · …"; one about the whole group (it was not
// asked, say) is not, and goes in the table's last rows.
const HOLE_ID = /^H\d+(-[A-Z])?$/;
const noteId = (n: string) => n.split(' · ')[0];
const isHoleNote = (n: string) => HOLE_ID.test(noteId(n));

export function JointHoles({ docId, onOpenPart, forRun = false, kind, table }: {
  docId: string;
  // A FIRST TAB, before the joints, and what it shows in their place — the Drilling table.
  table?: { label: string; body: ReactNode };
  // WHICH SUBPHASE LISTS THEM: Drilling holes the hardware joints, Placing covers the covers ones
  // (see Joint) — each with the assembled product last. The run's own copy lists every joint.
  kind?: 'holes' | 'covers';
  // Switch to the part's own view, with that part open.
  onOpenPart?: () => void;
  // THE RUN'S OWN COPY (see JointRunScene): it opens what the run opens, is measured and
  // photographed for it, and is the scene the run registers — the reader's copy is never touched.
  forRun?: boolean;
}) {
  const { t, language } = useLanguage();
  const { steps } = useAssemblySteps(docId);
  const { items } = usePartsRun(docId);
  const holes = useStepHoles(docId);
  const models = useModelSizes(docId).byPart;
  // THE JOINTS LISTED: hardware or covers, each a tab of its own (see Joint) — every one, for the run.
  const everyJoint = useJoints(docId);
  const joints = useMemo(() => (kind ? everyJoint.filter((j) => j.finds === (kind === 'holes')) : everyJoint), [everyJoint, kind]);
  const meshOf = useMeshOf(docId);
  const meshesKnown = useMeshOfLoaded(docId);
  // Each piece of hardware's model, by its hole — what draws it and what it is measured on.
  const fitOf = useHoleFit(docId);
  const { loaded: modelsLoaded } = usePartModelJobs();
  // The reader's joint is remembered for the manual; the run's copy holds its own, in memory.
  const remembered = usePlace(docId, jointPlaceSlot(kind));
  const own = useState('');
  const [saved, setSaved] = forRun ? own : remembered;
  // THE ASSEMBLED PRODUCT, the last entry: every joint at once — every component part, every
  // joint's holes, hardware and covers. It reads what the joints hold and runs nothing: each call
  // still photographs its own joint.
  const assembled = saved === ASSEMBLED && joints.length > 0;
  // THE FIRST TAB OPEN: no joint, and nothing of one loaded.
  const tabled = saved === TABLED && !!table && !forRun;
  const at = assembled || tabled ? -1 : Math.max(0, joints.findIndex((j) => j.id === saved));
  const joint = joints[at] ?? null;
  const selected = useMemo(() => (assembled ? joints : joint ? [joint] : []), [assembled, joints, joint]);
  const keyOf = (g: HoleGroup) => groupKey(steps[g.i]?.category ?? '', g.group);

  // BEING ASKED AGAIN: what a group holds is about to be replaced, so none of it is drawn — its
  // holes, its hardware, its notes — until the answer lands.
  const askedOf = (g: HoleGroup) => holes.asking.includes(keyOf(g));
  // Only a joint of its own waits for its answer: the assembled product draws the others meanwhile.
  const asked = !assembled && selected.some(askedOf);
  const found = useMemo(
    () => selected.filter((g) => !askedOf(g))
      .flatMap((g) => holesOfJoint(holes.byStep, steps, g))
      .filter((h, k, list) => list.findIndex((o) => o.id === h.id) === k)
      .sort((a, b) => Number(a.part) - Number(b.part) || a.id.localeCompare(b.id, undefined, { numeric: true })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected, holes.byStep, holes.asking, steps],
  );
  // WHERE A HOLE IS IN THE OPEN — a gap between parts, a side breaking out, a run past the last part
  // (see holeAir) — every hardware joint's holes, by hole id: counted on each tab, said under each hole.
  // Worked out on the meshes, so after them; not on the run's own copy.
  const everyHole = useMemo(() => joints.filter((g) => g.finds).flatMap((g) => holesOfJoint(holes.byStep, steps, g))
    .filter((h, k, list) => list.findIndex((o) => o.id === h.id) === k), [joints, holes.byStep, steps]);
  const air = useHoleAir(everyHole, items, models, meshOf, meshesKnown && !forRun);
  const airOf = (list: StepHole[]) => list.flatMap((h) => (air.get(h.id) ?? []).map((n) => `${h.id} · ${n}`));
  // EVERY JOINT READ AGAINST ITS TABLE ROWS, for the list; the open one's lines for under the model.
  const answer = useCoverPlacements(docId);
  // THE HOLES & COVERS RUN: which joint it is on, which it has still to reach.
  const requested = useJointRequests(docId);
  const runOn = requested.queue.length > 0 || requested.rolling;
  const checks = useMemo(() => joints.map((g) => checkJoint(
    // A COVERS JOINT IS NOT CHECKED FOR HOLES: those are its hardware joint's.
    g.finds ? g.rows : [], g.finds ? holesOfJoint(holes.byStep, steps, g) : [], items,
  )), [joints, holes.byStep, steps, items]);
  // A JOINT'S STATE: a hardware joint's holes, a covers joint's covers. Not looked at yet is the
  // holes not found, or the covers not placed.
  const coverFacts = useJointCoverFacts(docId);
  // THE COVERS A JOINT SHOWS, by the call that placed them (see useJointCoverFacts): a covers
  // joint, its own; a hardware joint, none. The assembled product shows every call's.
  const shownIn = (g: Joint | null): Shown[] => (g ? coverFacts.filter((f) => f.joint.id === g.id) : coverFacts)
    .map((f) => ({ owner: f.joint.id, rows: f.rows }));
  // WHAT DRILLING LEFT UNDONE ON A JOINT'S PARTS (see drillParts) — warnings like the rest: about
  // this joint's own holes, or about the part as a whole. A part is drilled with every joint's
  // holes at once, and another joint's hole is that joint's to say.
  const drillNotes = useDrillNotes(docId);
  const drillOf = (g: Joint) => {
    if (!g.finds) return [];
    const own = holesOfJoint(holes.byStep, steps, g);
    const ids = new Set(own.map((h) => h.id));
    return [...new Set([...g.drilled, ...own.map((h) => h.part)])].flatMap((part) => (drillNotes[part] ?? [])
      .filter((p) => !p.holes.length || p.holes.some((id) => ids.has(id)))
      .map((p) => ({ part, holes: p.holes.filter((id) => ids.has(id)), what: p.what })));
  };
  // ITS FIND HOLES NOTES — each headed by the hole its line enters by — once each: an answer stored
  // before a repeated line stopped filing its notes again carries copies, and notes headed by a
  // hole in another step's table. Warnings like the rest, so counted on its tab.
  const notesOf = (g: Joint) => {
    if (!g.finds) return [];
    const ids = new Set(holesOfJoint(holes.byStep, steps, g).map((h) => h.id));
    return [...new Set(holes.notes[keyOf(g)] ?? [])].filter((n) => ids.has(noteId(n)) || !HOLE_ID.test(noteId(n)));
  };
  // WHAT CANNOT BE DRAWN FOR WANT OF ITS MOUTH, counted on the tab as on its page: its hardware's
  // holes, or its covers on theirs.
  const unmeasuredIn = (g: Joint) => {
    if (g.finds) return unmeasuredOf(pieceHoles(holesOfJoint(holes.byStep, steps, g), (h) => !!fitOf(h))).length;
    // A COVERS JOINT: those its scene found no wood under, as last measured (see keepCoverMissed).
    return answer.byJoint[g.id]?.placements ? answer.byJoint[g.id]?.missed?.length ?? 0 : 0;
  };
  const status = joints.map((g, k): JointStatus => {
    const units = shownIn(g);
    const coverIssues = units.reduce((n, u) => n
      + checkCovers(u.rows, answer.byJoint[u.owner]?.placements ?? null,
        (id) => answer.byJoint[u.owner]?.into?.[id] ?? []).filter((c) => !c.ok).length, 0);
    // IN A RUN, ONLY THE JOINT IT IS ON SPINS: another joint showing a frame whose pads that joint
    // places would spin with it, and read as two joints run at once. Outside one, a joint spins
    // while any call it shows is out.
    const asking = runOn ? requested.current === g.id
      : askedOf(g) || units.some((u) => answer.busy === u.owner);
    return {
      state: asking ? 'asking'
        : requested.queue.includes(g.id) ? 'pending'
        : (g.finds ? !holes.byStep[steps[g.i]?.category ?? ''] : !answer.byJoint[g.id]?.placements) ? 'notRun' : 'checked',
      issues: checks[k].issues + coverIssues + drillOf(g).length
        + (askedOf(g) ? 0 : notesOf(g).length + unmeasuredIn(g) + (g.finds ? airOf(holesOfJoint(holes.byStep, steps, g)).length : 0)),
    };
  });
  const check = !asked ? checks[at] ?? null : null;
  // THE HOLES DRAWN, BY ID, each row of the table the switch of its own — a row is one run, and two
  // runs of one table line (two dowels) are two switches. None by default — the marks cover the
  // hardware they are the holes of — and a new answer or another joint opens that way again.
  const [shownLines, setShownLines] = useState<Set<string>>(new Set());
  const lit = shownLines;

  // THE JOINT'S COVERS, placed by a model call (see coverPlacementRun) on the places the page
  // knows — the holes found in it, and where its parts touch the floor. The same facts the badge
  // beside Find holes shows (see useJointCoverFacts).
  //
  // AND THE PADS OF ITS FRAMES, whichever call placed them (see shownIn): the covers drawn are
  // every call's it shows, each marked with the call it is.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const units = useMemo(() => (assembled ? shownIn(null) : joint ? shownIn(joint) : []), [assembled, joint, coverFacts]);
  // THE CALLS' ANSWERS, held as the same array until one of them changes — so what is measured
  // against them is not measured again because another joint's answer moved.
  const placedNow = units.map((u) => answer.byJoint[u.owner]?.placements ?? null);
  const placedRef = useRef(placedNow);
  if (placedNow.length !== placedRef.current.length || placedNow.some((p, i) => p !== placedRef.current[i])) {
    placedRef.current = placedNow;
  }
  const placed = placedRef.current;
  // NOT THE COVERS OF THE JOINT THE RUN IS ON: from its holes being asked until its covers land,
  // what it holds is about to be replaced — the same rule as its holes (see askedOf). A run stopped
  // brings them back, since nothing replaced them.
  const working = requested.queue[0];
  const covered = useMemo(() => units.flatMap((u, i): Drawn[] => {
    // Until its covers call is out: from then on they are that call's answers, drawn to be measured.
    if (u.owner === working && answer.busy !== u.owner) return [];
    const f = coverFacts.find((c) => c.joint.id === u.owner);
    const mine = placed[i];
    return f && mine ? coverMarks(mine, f.holes, f.spots)
      .map((m) => ({ ...m, unit: u.owner })) : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [units, coverFacts, placed, items, models, working, answer.busy]);

  // THE PARTS DRAWN — the group's own, and any part a hole it re-entered is in — each with its
  // kept model and where the product built it. The first stands at the viewer's origin, and the
  // others at their place against it.
  //
  // A MIRROR PAIR TWICE: its kept file is one hand, and the other is that file reflected across
  // the product's middle — where the product has it, holes and all.
  const parts = useMemo(() => {
    if (!selected.length) return [];
    // The assembled product: every component part the product has a model of.
    const ids = assembled
      ? items.flatMap((p, i) => (!isHardwareOrTool(p) && meshOf(String(i)) ? [String(i)] : []))
      : [...new Set([...selected.flatMap((g) => g.drilled), ...found.map((h) => h.part)])];
    const mx = productMiddleX(items, meshOf);
    return ids.flatMap((id) => {
      const job = meshOf(id);
      const b = builtBoundsOf(job);
      if (!job || !b) return [];
      const one = { id, key: id, job, b, mirror: false, hand: null as 'left' | 'right' | null };
      if (!isLeftRight(items[Number(id)]) || mx == null) return [one];
      const flipped = {
        min: [2 * mx - b.max[0], b.min[1], b.min[2]] as [number, number, number],
        max: [2 * mx - b.min[0], b.max[1], b.max[2]] as [number, number, number],
      };
      // WHICH HAND EACH IS, by where it stands (see keptHand).
      const kept = keptHand(b, mx);
      return [
        { ...one, hand: kept },
        { id, key: `${id}-r`, job, b: flipped, mirror: true, hand: kept === 'left' ? 'right' : 'left' },
      ] as typeof one[];
    });
  }, [assembled, selected, found, meshOf, items]);
  const centre = (b: { min: number[]; max: number[] }): Vec3 =>
    [(b.min[0] + b.max[0]) / 2, (b.min[1] + b.max[1]) / 2, (b.min[2] + b.max[2]) / 2];
  // A PART BY ITS NAME ALONE, as the joint tabs say it — no letter.
  const nameOf = (id: string) => namedIn(items[Number(id)], language) || `#${id}`;
  // A unit's name, with its hand where it is one of a pair: "Left side rail", as the tabs say it.
  const unitName = (p: { id: string; hand: 'left' | 'right' | null }) => sidedName(nameOf(p.id), p.hand, language);
  const instanceOf = (key: string) => (key === parts[0]?.key ? 'part' : `joint-${key}`);
  const src = (job: Job3D) => meshSrc(job.id, meshStamp(job));
  // A PART AS IT STOOD AT ONE POINT OF ITS HISTORY, picked under the viewer (see PartHistories): that
  // part drawn so, the rest as they stand. Null: every part as it stands.
  // `spots`: where that change cut, plugged or drilled, drawn over it in red (see zoneMarks).
  const [viewing, setViewing] = useState<({ job: number } & PartView) | null>(null);
  // Whether one of its parts is being changed from its history (see PartHistories).
  const [reshaping, setReshaping] = useState(false);
  useEffect(() => { setViewing(null); }, [joint?.id]);
  // EVERY PART AS IT STANDS, every joint's holes in it (Louis 2026-10-29): no copy of it per joint.
  // At its newest state, the part as it stands: the live mesh, not a copy of it at that count.
  const partSrc = (p: { job: Job3D }) => (viewing?.job === p.job.id && viewing.at != null && viewing.at < editsOf(p.job).length
    ? meshAtSrc(p.job.id, viewing.at, meshStamp(p.job)) : src(p.job));
  // ASSEMBLED OR EXPLODED, THE WHOLE PRODUCT'S WAY: the parts spread out from their middle
  // by explodeShifts, over EXPLODE_MS and eased the same (see explodePieces) — and each piece of
  // hardware backs out of its hole as they go, all of it at once. `e` is how far apart, 0 to 1.
  const [apart, setApart] = useState(false);
  const [eWhole, setE] = useState(0);
  const eNow = useRef(0);
  const spreading = useRef(0);
  const spreadTo = (to: number, from = eNow.current) => {
    cancelAnimationFrame(spreading.current);
    eNow.current = from; setE(from);
    const t0 = performance.now();
    const tick = () => {
      const k = Math.min(1, (performance.now() - t0) / EXPLODE_MS);
      eNow.current = from + (to - from) * smoothstep(k);
      setE(eNow.current);
      if (k < 1) spreading.current = requestAnimationFrame(tick);
    };
    spreading.current = requestAnimationFrame(tick);
  };
  useEffect(() => () => cancelAnimationFrame(spreading.current), []);
  useEffect(() => {
    cancelAnimationFrame(spreading.current);
    eNow.current = 0; setE(0); setApart(false);
  }, [joint?.id]);
  const e = eWhole;
  const spread = e;
  const toggle = { on: apart, set: (on: boolean) => { setApart(on); spreadTo(on ? 1 : 0); } };
  // Which way each piece of hardware goes in and how long it is, off its own mesh (see fitAxis) —
  // worked out below, and read here by how a joint comes apart.
  const [axes, setAxes] = useState<Map<string, FitAxis>>(new Map());
  // APART, THE WHOLE PRODUCT'S WAY out from its middle; A JOINT'S along its hardware's lines, so the
  // gap opens where the hardware is (see jointSpread).
  const shifts = useMemo(() => (assembled
    ? explodeShifts(parts.map((p) => ({
      min: { x: p.b.min[0], y: p.b.min[1], z: p.b.min[2] },
      max: { x: p.b.max[0], y: p.b.max[1], z: p.b.max[2] },
    })))
    : jointSpread(parts, found, items, (h) => {
      const fit = fitOf(h);
      return (fit && axes.get(fit.src)?.len) ?? null;
    })), [assembled, parts, found, items, fitOf, axes]);
  // Where a unit stands against the first part, spread by `e`.
  const unitAt = (k: number): Vec3 => {
    const o = centre(parts[0].b);
    const c = centre(parts[k].b);
    return [c[0] - o[0] + (shifts[k].x - shifts[0].x) * spread, c[1] - o[1] + (shifts[k].y - shifts[0].y) * spread,
      c[2] - o[2] + (shifts[k].z - shifts[0].z) * spread];
  };
  // THE PRODUCT'S MIDDLE: the middle of every component part's box, both hands of a pair — which
  // is the whole product's own box, the product being its component parts. The origin is there,
  // as "Whole product" draws it, and the first part stands where the product has it, spread too.
  const productMiddle = useMemo((): Vec3 | null => {
    const mx = productMiddleX(items, meshOf);
    const boxes = items.flatMap((p, i) => {
      if (isHardwareOrTool(p)) return [];
      const b = builtBoundsOf(meshOf(String(i)));
      if (!b) return [];
      return isLeftRight(p) && mx != null
        ? [b, { min: [2 * mx - b.max[0], b.min[1], b.min[2]], max: [2 * mx - b.min[0], b.max[1], b.max[2]] }]
        : [b];
    });
    if (!boxes.length) return null;
    const lo = (i: number) => Math.min(...boxes.map((b) => b.min[i]));
    const hi = (i: number) => Math.max(...boxes.map((b) => b.max[i]));
    return [(lo(0) + hi(0)) / 2, (lo(1) + hi(1)) / 2, (lo(2) + hi(2)) / 2];
  }, [items, meshOf]);
  const firstAt = useMemo((): Vec3 | undefined => {
    if (!parts[0] || !productMiddle) return undefined;
    const c = centre(parts[0].b);
    return [c[0] - productMiddle[0] + shifts[0].x * spread, c[1] - productMiddle[1] + shifts[0].y * spread,
      c[2] - productMiddle[2] + shifts[0].z * spread];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parts, productMiddle, shifts, e]);

  // THE HARDWARE, STOOD IN ITS HOLES AS PARTS OF THE SCENE — a row each, with its box, rulers,
  // origin and face words like every other part. ONCE PER PIECE, in the hole it enters by: a
  // screw's line holds two holes, the one its head is in (through) and the one its tip ends in;
  // a dowel's two, the first declared taking it. And on every unit of a pair. The barrel nut
  // across a screw's line is on that line too, and a piece of its own: a line is read per hardware.
  const fitted = useMemo(() => pieceHoles(found, (h) => !!fitOf(h)), [found, fitOf]);
  // AND THE WASHERS, each on the piece its joint's rows put it on (HardwareInteraction.washers) — the
  // Drilling table's "(+ Spring washer (F))", drawn. Every one, a model or not: one without is said.
  const washers = useMemo(() => {
    const on = Object.assign({}, ...selected.flatMap((g) => (g.finds ? g.rows : [])).map((r) => r.washers ?? {})) as Record<string, string>;
    return fitted.flatMap((h) => Object.entries(on).filter(([, id]) => id === h.via)
      .map(([w]): Washer => ({ ...h, id: `${h.id}+${w}`, via: w, through: false, depthMm: 0, rides: h })));
  }, [selected, fitted]);
  const drawnWashers = useMemo(() => washers.filter((w) => !!fitOf(w)), [washers, fitOf]);
  // Where a cover with no hole to sit on (a foot's pad) meets the wood under its box face, off the
  // part's mesh (each piece's axis, above). A cover on a hole is never measured here, however it was
  // moved: the mesh is the drilled one by now, and a ray down the bore met no wood — it takes its
  // hole's (see StepHole.surfaceMm).
  const [mouths, setMouths] = useState<Map<string, number>>(new Map());
  // WHICH COVERS THEY WERE WORKED OUT FOR — the covers are where they go once it is these.
  const [placedFor, setPlacedFor] = useState<CoverMark[] | null>(null);
  useEffect(() => {
    let gone = false;
    void (async () => {
      const { THREE, GLTFLoader } = await loadDeps();
      const loader = new GLTFLoader();
      const got = new Map<string, FitAxis>();
      for (const h of [...fitted, ...drawnWashers, ...covered]) {
        const fit = fitOf(h);
        if (!fit || got.has(fit.src)) continue;
        const axis = await fitAxisOf(THREE, loader, fit).catch(() => null);
        if (axis) got.set(fit.src, axis);
      }
      const solids = new Map<string, Solid | null>();
      const deep = new Map<string, number>();
      // EVERY COVER, where it first touches the wood (see firstContact).
      for (const h of covered) {
        const part = parts.find((p) => p.id === h.part && !p.mirror);
        if (!part) continue;
        if (!solids.has(part.id)) {
          solids.set(part.id, await loadSolid(part.job, items[Number(part.id)]?.size).catch(() => null));
        }
        const solid = solids.get(part.id);
        const local = placeOn(h, part.b);
        if (!solid || !local) continue;
        const n = faceNormal(h.face);
        const c = centre(part.b);
        const fit = fitOf(h);
        const reach = (fit && got.get(fit.src)?.reach) ?? 0;
        const hit = firstContact(solid, [c[0] + local[0], c[1] + local[1], c[2] + local[2]], n, reach);
        if (hit != null) deep.set(h.id, Math.max(0, hit));
      }
      if (!gone) { setAxes(got); setMouths(deep); setPlacedFor(covered); }
    })();
    return () => { gone = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitted, drawnWashers, found, covered, parts, fitOf]);

  // THE COVERS THE RAY FOUND NO WOOD UNDER, kept with each call's answer once measured — so its
  // tab counts them without being opened. Only a call's whole answer, measured on these covers.
  useEffect(() => {
    if (placedFor !== covered) return;
    units.forEach((u, i) => {
      const all = placed[i];
      if (!all) return;
      keepCoverMissed(docId, u.owner, all, covered
        .filter((c) => c.unit === u.owner && mouths.get(c.id) == null)
        .map((c) => c.id));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placedFor, covered, mouths, units, placed]);

  // WHERE THE WOOD BEGINS UNDER A PIECE'S BOX FACE: its hole's, as the line measured it; a
  // cover off any hole, as measured above. Unknown is not drawn (see unmeasured), never the box face.
  const mouthOf = (h: StepHole) => h.surfaceMm ?? mouths.get(h.id);
  // WHERE EACH SCREW WITH A WASHER STANDS, by its hole and unit, and how deep its middle is — the
  // washer is drawn under its head from there, so it goes wherever the screw goes.
  const hardware = useMemo(() => {
  const screwAt = new Map<string, { at: Vec3; down: number }>();
  return [...fitted, ...drawnWashers, ...covered].flatMap((raw) => {
    const fit = fitOf(raw);
    const axis = fit && axes.get(fit.src);
    const mouth = mouthOf(raw);
    if (!fit || !axis || mouth == null) return [];
    const rides = (raw as Washer).rides;
    // A PIECE WITH WASHERS UNDER ITS HEAD stands out of the wood by their thickness: its head bears
    // on them, not on the wood (read as a shallower sink — StepHole.sunkMm).
    const lift = rides ? 0 : drawnWashers.filter((w) => w.rides === raw)
      .reduce((sum, w) => sum + (axes.get(fitOf(w)?.src ?? '')?.len ?? 0), 0);
    const h: StepHole = lift ? { ...raw, sunkMm: (raw.sunkMm ?? 0) - lift } : raw;
    // A PIECE ACROSS ANOTHER'S LINE (a barrel nut across its screw's): turned so its hole faces it.
    const crossed = h.line && h.line !== h.id ? found.find((o) => o.id === h.line && o.via !== h.via) : undefined;
    const crossedFit = crossed && fitOf(crossed);
    const crossedAxis = crossedFit && axes.get(crossedFit.src);
    const across = crossed && crossedFit && crossedAxis ? acrossOf(fit, crossedFit, crossedAxis, crossed) : null;
    return parts.map((p, k) => ({ p, k })).filter(({ p }) => holeOnUnit(h, p.id, p.hand)).flatMap(({ p, k }) => {
      const at = placeOn(h, p.b);
      if (!at) return [];
      // Every piece stands back by the toggle as the whole product's do (see `back`). THE SCREWS
      // UNDER A COVERS JOINT'S BUTTONS stand home, still: they are another joint's.
      const home = !!joint && !joint.finds && !('cover' in h);
      // The reflected unit: its own place and normal are the kept one's across x.
      const flip = (v: Vec3): Vec3 => (p.mirror ? [-v[0], v[1], v[2]] : v);
      const local = flip(at);
      const n = flip(faceNormal(h.face));
      const inward: Vec3 = [-n[0], -n[1], -n[2]];
      // AS DEEP AS ITS HOLE, below a sunk head, as the assembly phase is told it (see restDown) — a
      // surface cover, whose "hole" is 0 deep, not in at all: it sits on the face. AND WHERE THE
      // COVERS CALL MOVED A COVER OUT OF ITS PART, less (see Placement.outMm).
      const down = restDown(h, axis.len, mouth) - ((h as CoverMark).outMm ?? 0);
      // Exploded, it stands CLEAR OF THE WOOD (Louis 2026-10-29): backed out by as deep as its far
      // end goes — its middle's depth and half its length, a sunk head's depth included — and a
      // little more. By its own length alone, a screw sunk 18 mm still had its tip in the rail. And
      // A COVER OVER A HOLE clear of what it covers too: a button came out through its screw.
      const capped = (h as CoverMark).cover === 'hole' ? fitted.find((f) => f.id === (h as CoverMark).of) : undefined;
      const cappedFit = capped && fitOf(capped);
      const under = cappedFit ? axes.get(cappedFit.src)?.len ?? 0 : 0;
      const back = home ? 0 : (Math.max(0, down + axis.len / 2) + BACK_OFF_MM + (under ? under + BACK_OFF_MM : 0)) * e;
      const u = unitAt(k);
      const stood = [0, 1, 2].map((i) => u[i] + local[i] + inward[i] * down + n[i] * back) as unknown as Vec3;
      // A WASHER: its screw's place, moved along the hole from the screw's middle to its own.
      const screw = rides ? screwAt.get(`${rides.id}-${p.key}`) : undefined;
      if (rides && !screw) return [];
      if (!rides && lift) screwAt.set(`${h.id}-${p.key}`, { at: stood, down });
      return [{
        id: `hw-${h.id}-${p.key}`,
        group: ('cover' in h ? 'cover' : 'hardware') as ViewerOther['group'],
        via: h.via,
        unit: (h as Drawn).unit ?? null,
        // A cover, said by what it covers — ten wood buttons are ten rows of the collisions table.
        cover: 'cover' in h ? `${nameOf(h.via!)} · ${(h as CoverMark).cover === 'hole'
          ? t('notice.covers.onHole').replace('{id}', (h as CoverMark).of ?? '') : t('notice.covers.onFoot')}${p.hand
          ? ` · ${t(p.hand === 'left' ? 'notice.assembly.faceLeft' : 'notice.assembly.faceRight').toLowerCase()}` : ''}` : null,
        src: fit.src,
        label: p.hand
          ? `${nameOf(h.via!)} · ${t(p.hand === 'left' ? 'notice.assembly.faceLeft' : 'notice.assembly.faceRight').toLowerCase()}`
          : nameOf(h.via!),
        builtMm: fit.built,
        dims: fit.dims,
        trueScale: fit.trueScale,
        at: screw
          ? [0, 1, 2].map((i) => screw.at[i] + inward[i] * (down - screw.down + WASHER_GAP_MM * e)) as unknown as Vec3
          : stood,
        // A PIECE ALIKE AT BOTH ENDS (a pad, a dowel) turned the short way round — either way in is
        // right, and the long way turns its own face words upside down for nothing.
        // AND A COVER THE ANSWER TURNED OVER, the other way in (see Placement.flip).
        rot: [...holeTurn(axis, inward, !!(h as CoverMark).flip, across)] as [number, number, number, number],
      }];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });
  }, [fitted, drawnWashers, found, covered, fitOf, axes, mouths, parts, shifts, e, language, joint, items]);

  // DO THE COVERS GO INTO THE PARTS? Measured where they go — not while they stand out of their
  // places (see coverCollisions).
  const coverUnits = useMemo(
    () => hardware.flatMap((w) => (w.cover ? [{ id: w.id, label: w.cover }] : [])),
    [hardware],
  );
  const partUnits = useMemo(() => parts.map((p) => ({ id: instanceOf(p.key), label: unitName(p) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parts, language]);
  const collideKey = covered.length && placedFor === covered && e === 0
    ? hardware.map((w) => `${w.id}:${w.at.join(',')}:${w.rot.join(',')}`).join(';') : null;
  const collisions = useCoverCollisions({
    covers: coverUnits, parts: partUnits, key: collideKey, of: placed,
  });
  // EACH COVER AGAINST THE ROWS, and how deep the ones on screen go into a part — by the cover,
  // off the collisions' labels, which are the drawn covers' own.
  const intoOf = (u: Shown, id: string) => {
    const labels = new Set(hardware.filter((w) => w.cover && w.unit === u.owner && w.via === id).map((w) => w.cover));
    return collisions.rows.filter((r) => labels.has(r.a)).map((r) => ({ part: r.b, mm: r.depth }));
  };
  const coverLines = useMemo(() => units.flatMap((u, i) => checkCovers(u.rows, placed[i], (id) => intoOf(u, id))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [units, placed, hardware, collisions.rows]);
  // AND KEPT WITH THE ANSWER, once measured where the covers go: the joint's tab counts them from
  // there (see status). Only a call's whole answer — a frame's pads seen from another joint are some.
  useEffect(() => {
    if (collisions.checking || !collideKey) return;
    units.forEach((u, i) => {
      const all = placed[i];
      if (!all) return;
      keepCoverInto(docId, u.owner, all, Object.fromEntries([...new Set(u.rows.flatMap((r) => r.covers))]
        .map((id) => [id, intoOf(u, id)]).filter(([, deep]) => deep.length)));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collisions.checking, collideKey, collisions.rows, units, placed]);

  // THE SCENE THE HOLES & COVERS RUN USES — the run's copy only — registered every render with
  // what the page holds now (see registerJointView): the run is held outside this component, so it
  // is not ended by this copy going, and coming back does not start a second one.
  const run = useHoles(docId);
  const token = useRef({}).current;
  if (forRun) {
    registerJointView(docId, token, {
      jointId: joint?.id, everyJoint, coverFacts, holesBusy: holes.busy, run, models, modelsLoaded,
      open: setSaved,
    });
  }
  useEffect(() => () => dropJointView(docId, token), [docId, token]);

  const others = useMemo<ViewerOther[]>(() => [
    ...parts.slice(1).map((p, k) => ({
      id: instanceOf(p.key), src: partSrc(p), label: unitName(p), builtMm: builtBoxOf(p.job),
      at: unitAt(k + 1), mirror: p.mirror,
    })),
    ...hardware,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [parts, language, shifts, e, hardware, found, viewing]);

  useEffect(() => { setShownLines(new Set()); }, [found, parts]);
  // THE COVERS WHOSE ROWS ARE ON, outlined on the model — by cover part, every one of them drawn.
  const [litCovers, setLitCovers] = useState<Set<string>>(new Set());
  useEffect(() => { setLitCovers(new Set()); }, [joint?.id]);
  const outlined = useMemo(
    () => hardware.filter((w) => w.cover && w.via != null && litCovers.has(w.via)).map((w) => w.id),
    [hardware, litCovers],
  );

  // THE HOLES OF THE LINES SWITCHED ON, EACH ON ITS OWN UNIT, in its hardware's colour. Held in a ref the scene
  // reads every frame (see PartViewer.holeMarks). Where the wood begins under the box face is the
  // line's own measurement (see `mouths`), so the mark and the hardware start at the same place.
  const marks = useMemo<HoleMark[]>(() => {
    const hue = huesByVia(found);
    // On its own unit — every unit of its part, for a hole from before a pair's line was one per
    // unit. The reflected one draws its marks reflected with it.
    return found.filter((hole) => lit.has(hole.id)).flatMap((hole) => parts.filter((p) => holeOnUnit(hole, p.id, p.hand)).flatMap((part) => {
      const at = holePlace(hole, dimsOf(part.b));
      if (!at) return [];
      return [{
        on: instanceOf(part.key),
        face: hole.face,
        u: at.u,
        v: at.v,
        diameter: holeWidthOf(hole, items, models) ?? 0,
        depth: hole.through ? hole.woodMm ?? null : holeDepthOf(hole, models).mm,
        through: hole.through,
        colour: hue.get(hole.id) ?? SAID_COLOURS[0],
        ...(hole.surfaceMm != null ? { surface: hole.surfaceMm } : {}),
      }];
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [found, parts, items, models, lit]);
  // THE ZONE A CHANGE TOUCHED, in red (Louis 2026-10-29): each of its bores, whichever joint it is.
  // ON EVERY UNIT OF ITS PART (Louis 2026-10-29): a mirror pair's two hands are one file, so a bore cut
  // for the left rail is in the right one too.
  const zoneMarks = useMemo<HoleMark[]>(() => (viewing ? parts.filter((p) => p.job.id === viewing.job)
    .flatMap((part) => viewing.spots
      .map((z) => ({
      on: instanceOf(part.key), face: z.face as HoleMark['face'], u: z.u, v: z.v, diameter: z.diameter,
      // No colour: the bore alone in the app's red (see HoleMark.colour), no fill's ball round it.
      depth: z.depth, through: z.through,
      }))) : []),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [viewing, parts]);
  const marksNow = useRef(marks);
  useEffect(() => { marksNow.current = [...marks, ...zoneMarks]; }, [marks, zoneMarks]);
  const readMarks = useRef(() => marksNow.current).current;

  // THE PIECES NOT DRAWN FOR WANT OF THEIR MOUTH: a hole found before it was measured, a cover
  // whose wood the ray missed — once the covers have been measured, not while they are.
  const unmeasured = useMemo(() => [
    ...fitted.filter((h) => h.surfaceMm == null),
    ...(placedFor === covered ? covered.filter((h) => mouthOf(h) == null) : []),
  ].map((h) => `${h.id} · ${t('notice.holes.noSurface')}`)
    // AND A SCREW WHOSE MODEL GAVE NO THREAD TO TURN IT BY (see threadLeadOf), once its axis is read.
    .concat(fitted.filter((h) => {
      const axis = axes.get(fitOf(h)?.src ?? '');
      return !!axis && unthreaded(axis, items[Number(h.via)]);
    }).map((h) => `${h.id} · ${t('notice.joints.unthreaded')}`))
    // AND A WASHER WITH NO MODEL KEPT: not drawn, and said under its screw's hole.
    .concat(washers.filter((w) => !fitOf(w)).map((w) => `${w.rides.id} · ${t('notice.joints.washerNoModel').replace('{w}', nameOf(w.via!))}`)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [fitted, washers, covered, placedFor, mouths, axes, language]);
  // THE NOTES ABOUT THE HOLES IN THIS TABLE (see notesOf), in the table's order.
  const notes = useMemo(() => {
    // AND A HOLE NO GESTURE CLAIMS (see relinkHoles): drilled and drawn, played over the whole
    // joint rather than in a sub-step of its own, because none says which hand it works on.
    const untimed = assembled ? [] : found.filter((h) => !h.at).map((h) => `${h.id} · ${t('notice.joints.untimed')}`);
    return [...new Set([...selected.flatMap((g) => (askedOf(g) ? [] : notesOf(g))), ...unmeasured, ...airOf(found), ...untimed])]
      .sort((a, b) => noteId(a).localeCompare(noteId(b), undefined, { numeric: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, found, holes.notes, holes.asking, steps, unmeasured, air, assembled, language]);

  // THE ASSEMBLED PRODUCT'S MARK: pending while a Holes & covers run goes — it is every joint, so it
  // is done only when the last of them is; being asked where any joint is outside a run; otherwise
  // everything still to look at, a joint never run counted as one.
  const wholeStatus: JointStatus = {
    state: runOn ? 'pending' : status.some((s) => s.state === 'asking') ? 'asking' : 'checked',
    issues: status.reduce((n, s) => n + (s.state === 'notRun' ? 1 : s.issues), 0),
  };
  const openPart = (part: string) => {
    if (!onOpenPart) return;
    const i = Number(part);
    writePlace(docId, 'part', partSearchKey(items[i]) || `#${i}`);
    onOpenPart();
  };

  // WHAT DRILLING THIS JOINT'S PARTS LEFT UNDONE, in its table: about a hole, in that hole's row;
  // about the part as a whole, in a last row of its own.
  const drilled = !assembled && joint ? drillOf(joint) : [];
  const partWide = drilled.filter((p) => !p.holes.length)
    .map((p) => t('notice.joints.drillNote').replace('{part}', nameOf(p.part)).replace('{what}', p.what));

  if (!joints.length) {
    return <p className="text-center text-sm text-slate-400">{t('notice.joints.none')}</p>;
  }
  // NOTHING OF THE LAST ANSWER WHILE A RUN WILL REPLACE IT, on the reader's view: a joint the run
  // is on or has still to reach, and the assembled product, which mixes all of them — a spinner in
  // the viewer's place, and no table. The run drills parts every joint shares as it goes, so the
  // old answer would stand on meshes that are no longer the ones it was found on.
  const awaiting = !forRun && runOn && (assembled
    || (joint != null && (requested.current === joint.id || requested.queue.includes(joint.id))));
  const view = awaiting ? (
    <div className={`mx-auto flex max-w-4xl items-center justify-center rounded-2xl border border-slate-200 bg-white ${VIEWER_BOX}`}>
      <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
    </div>
  ) : (
    <div className="flex min-w-0 flex-col gap-4">
      {selected.length > 0 && (
        <>
          {/* THE JOINT'S OWN CARD: its viewer, the toggle on it. THE ASSEMBLED PRODUCT'S TOO (Louis
              2026-10-29), its controls inside the white rather than on the page. */}
          <div className={forRun ? 'contents' : PLAY_CARD}>
          {parts[0] ? (
            <div className="relative">
            {/* A PART BEING CHANGED FROM ITS HISTORY (Louis 2026-10-29): said over the picture at once,
                not only once the new mesh starts loading. */}
            {reshaping && (
              <div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-white/60">
                <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
              </div>
            )}
            <PartViewer
              key={parts.map((p) => p.key).join(',')}
              src={partSrc(parts[0])}
              label={unitName(parts[0])}
              builtMm={builtBoxOf(parts[0].job)}
              others={others}
              holeMarks={readMarks}
              // ASSEMBLED / EXPLODED on every reader's view — a joint being drilled too, as the hardware's
              // own viewer has it. Not on the run's copy, which is photographed as the product stands.
              apart={forRun ? undefined : toggle}
              standAt={firstAt}
              keepSolid={(id) => id.startsWith('hw-')}
              highlight={outlined}
              collide={collisions.collide}
              clashPaint={collisions.paint}
              // ITS CONTROLS IN A ROW ABOVE THE PICTURE (Louis 2026-10-29): over it, the pose toggle in the
              // middle ran into the draw mode beside it, and the four groups covered the parts.
              controlsAbove
              className={forRun ? `mx-auto max-w-4xl ${VIEWER_BOX}` : VIEWER_BOX}
            />
            </div>
          ) : !meshesKnown ? (
            // NOT "NONE OF THESE PARTS HAS A MODEL" before the models are even known — the list,
            // or what resolves a part to it, is still on its way.
            <div className={`flex items-center justify-center ${VIEWER_BOX}`}>
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : <p className="text-center text-sm text-slate-400">{t('notice.joints.noModels')}</p>}
          {/* AND THE HISTORY OF EACH OF ITS PARTS, under its viewer (see PartHistories). A joint's: the
              assembled product's would be every part of it. */}
          {!assembled && !forRun && parts.length > 0 && (
            <PartHistories
              parts={parts.filter((p) => !p.mirror).map((p) => ({
                id: p.id, label: nameOf(p.id), job: p.job, holes: holesOfPart(holes.byStep, p.id).map((h) => h.hole),
              }))}
              items={items}
              models={models}
              joint={new Set(found.map((h) => h.id))}
              viewing={viewing}
              // SEEN BEFORE IT IS TAKEN OFF (Louis 2026-10-29): the part as that change left it, and the
              // holes it is about outlined on it.
              onView={(job, view) => setViewing(view && { job, ...view })}
              onBusy={setReshaping}
            />
          )}
          </div>
          {/* WHAT THE TABLE EXPECTS AND WHAT WAS FOUND — a spinner while the joint is asked again.
              Nothing under the assembled product: the joint cards above already say how each stands. */}
          {asked ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : assembled ? null : check && (check.lines.length || found.length || coverLines.length) ? (
            <ExpectedLines
              rows={joint?.finds ? joint.rows : []}
              sizes={models}
              check={check}
              covers={coverLines}
              pickedCovers={litCovers}
              onPickCover={(id) => setLitCovers((was) => {
                const next = new Set(was);
                if (!next.delete(id)) next.add(id);
                return next;
              })}
              coverNotes={[...new Set(units.map((u) => answer.byJoint[u.owner]?.passes.at(-1)?.notes ?? ''))]
                .filter(Boolean).join(' ')}
              items={items}
              picked={shownLines}
              onPick={(k) => setShownLines((was) => {
                const next = new Set(was);
                if (!next.delete(k)) next.add(k);
                return next;
              })}
              onOpenPart={openPart}
              notes={[...notes.filter(isHoleNote), ...drilled.flatMap((p) => p.holes.map((id) => `${id} · ${p.what}`))]}
              partNotes={[...notes.filter((n) => !isHoleNote(n)), ...partWide]}
            />
          ) : (
            <>
              <p className="text-center text-sm text-slate-400">{t('notice.joints.noHoles')}</p>
              {[...notes.filter((n) => !isHoleNote(n)), ...partWide].map((n) => <p key={n} className="text-center text-sm text-amber-600">{n}</p>)}
            </>
          )}
        </>
      )}
    </div>
  );
  // THE JOINTS OVER THE ONE OPEN.
  return (
    <div className="flex flex-col gap-4">
      <JointList
        groups={joints}
        items={items}
        at={at}
        status={status}
        onSelect={(k) => setSaved(joints[k].id)}
        whole={{ on: assembled, status: wholeStatus, onSelect: () => setSaved(ASSEMBLED) }}
        first={table && !forRun ? { label: table.label, on: tabled, onSelect: () => setSaved(TABLED) } : undefined}
      />
      {tabled ? table!.body : view}
    </div>
  );
}

// THE HOLES & COVERS RUN'S OWN SCENE, while a run is going: a second joint view, drawn full size
// off the screen, which the run opens joints in and reads each joint's holes and covers' facts off.
// The reader's view is theirs to walk meanwhile. It costs the run a load of the
// joint's models it was making anyway, and the page the memory of two joints at once.
//
// OFF THE SCREEN AND NOT HIDDEN: a canvas under `display: none` has no size, and draws nothing to
// photograph. And A PORTAL ON THE BODY for the same reason: a phase not on screen is `hidden`
// (see NoticeViewer), and the run goes on while you read another one.
export function JointRunScene({ docId }: { docId: string }) {
  const going = useJointRequests(docId).queue.length > 0;
  if (!going) return null;
  return createPortal(
    <div aria-hidden inert className="pointer-events-none fixed left-[-10000px] top-0 w-[56rem]">
      <JointHoles docId={docId} forRun />
    </div>,
    document.body,
  );
}
